import { ApiError } from "../api/generated/core/ApiError";
import { SubscriptionView } from "../api/generated/models/SubscriptionView";
import i18next from 'i18next';

export const helper = {
  formatDate: (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  },
  // Date without time (DD.MM.YYYY) - matches the format used in backend emails.
  formatDateOnly: (dateString: string): string => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  },
  formatPrice: (money: { amount: number; currency: string }, months: number = 1) => {
    const amount = money.amount / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: money.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  },
  formatPriceCents: (priceCents: number, currency: string = 'EUR') => {
    const amount = priceCents / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  },
  getParam: (param: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  },
  validateEmail: (value: string): string => {
    if (!value) {
      return i18next.t('validation_email_required');
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      return i18next.t('validation_email_invalid');
    }
    return '';
  },
  // Generic API error message handler
  getApiErrorMessage: (error: any): string => {
    if (error?.body?.error) {
      return error.body.error;
    }
    if (error?.message) {
      return error.message;
    }
    return i18next.t('error_unexpected');
  },
  // Check if subscription is active and not expired
  isSubscriptionActive: (subscription: SubscriptionView): boolean => {
    if (!subscription.isActive) return false;
    if (!subscription.expired) return subscription.isActive;
    return new Date(subscription.expired) > new Date();
  },
  // Check whether a subscription carries an enabled product option (value === 'true')
  hasProductOption: (subscription: SubscriptionView, slug: string): boolean =>
    subscription.options?.some((o) => o.slug === slug && o.value === 'true') ?? false,
  // Calculate expiry date from duration in seconds
  calculateExpiryDate: (durationSec: number): Date => {
    return new Date(Date.now() + durationSec * 1000);
  },
  isNotFoundError: (error: Error): boolean => {
    return error?.message?.toLowerCase().includes('not found') || error?.message?.toLowerCase().includes('not-found');
  },
  parseApiErrorBody: (error: ApiError): any => {
    if (typeof error.body === 'string') {
      try {
        return JSON.parse(error.body);
      } catch (parseError) {
        console.log('Failed to parse error body JSON:', parseError);
        return null;
      }
    }
    return error.body;
  },
  // True when the API rejected a request because the active plan lacks the
  // required feature (HTTP 403, body `errorCode === 'subscription_upgrade_required'`).
  // Branch on this single machine-readable signal to drive the "Upgrade" CTA -
  // never on the human-readable `error` text.
  isUpgradeRequiredError: (error: unknown): boolean => {
    if (!(error instanceof ApiError)) return false;
    const body = helper.parseApiErrorBody(error);
    return !!body && body.errorCode === 'subscription_upgrade_required';
  },
};
